import {css, StyleSheet} from 'aphrodite';
import 'moment-timezone';
import React, {useEffect, useState} from 'react';

import BenchlingConfig from 'BenchlingConfig';
import DataConnector from 'connect/DataConnector';
import {EXTERNAL_FILE_TYPE} from 'constants/PreviewTypeConstants';
import ModelHubExecutionCountNotice from 'model-hub/ModelHubExecutionCountNotice';
import {
  getModelDisplayName,
  ScientificModelPreferences,
} from 'model-hub/scientific-models/ScientificModelConstants';
import AaSequenceStore from 'molbio/aa-sequences/AaSequenceStore';
import QualityDataLegend from 'molbio/aa-sequences/alphafold/QualityDataLegend';
import {ProteinStructureExternalFile} from 'molbio/aa-sequences/util/AaSequenceConstants';
import {FONT_PROXIMA_13} from 'styles/FontConstants';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {GRAY_DARKER} from 'ui-library/Colors';
import {FloatingInteraction, FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import Popover from 'ui-library/floating-ui/Popover';
import usePreference from 'ui-library/hooks/usePreference';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

import {
  downloadStructureFile,
  findCoordinateStructureById,
  getStructuresForVersion,
  getStructureInfoFromVersions,
} from './AlphaFoldTabUtils';
import StructurePredictionInfo from './StructurePredictionInfo';

export interface AlphaFoldOptionsProps {
  proteinId: string;
}

interface EnhancedAlphaFoldOptionsProps {
  structureFile: ProteinStructureExternalFile | null;
  model: string | null;
  modelVersion: string | null;
}

class AlphaFoldOptionsConnector extends DataConnector<AlphaFoldOptionsProps, EnhancedAlphaFoldOptionsProps> {
  override shouldSubscribeToRedux = false;
  override stores = [AaSequenceStore];

  // Track the last seen version ID to detect version changes
  private lastSeenVersionId: number | null = null;

  enhanceProps(): {
    structureFile: ProteinStructureExternalFile | null;
    model: string | null;
    modelVersion: string | null;
    proteinId: string;
    structureProteinVersionId: number | null;
  } {
    const aaSequenceStore = this.aaSequenceStore();
    const aaSequence = aaSequenceStore.getAaSequenceById(this.props.proteinId)!;
    const proteinId = this.props.proteinId;
    const currentlyViewedProteinVersionId = aaSequence.version.id;

    // Track if this is the first time we're seeing this version
    const versionChanged = this.lastSeenVersionId !== currentlyViewedProteinVersionId;
    this.lastSeenVersionId = currentlyViewedProteinVersionId;

    const versions = aaSequenceStore.getVersionsById(proteinId);
    const currentCoordinateStructureId = aaSequenceStore.getCoordinateStructureId(
      currentlyViewedProteinVersionId
    );

    // If there's a current coordinate structure ID, use that specific prediction
    if (currentCoordinateStructureId) {
      const allCoordinateStructures = getStructuresForVersion(versions, currentlyViewedProteinVersionId);
      const selectedCoordinateStructure = findCoordinateStructureById(
        allCoordinateStructures,
        currentCoordinateStructureId
      );

      if (selectedCoordinateStructure) {
        return {
          structureFile: selectedCoordinateStructure.structureFile,
          model: selectedCoordinateStructure.model,
          modelVersion: selectedCoordinateStructure.modelVersion,
          proteinId,
          structureProteinVersionId: selectedCoordinateStructure.versionId,
        };
      }
    }

    // Fallback to getStructureInfoFromVersions when switching to a version for the first time
    // This handles initial load and version switching
    if (versionChanged) {
      const structureInfo = getStructureInfoFromVersions(currentlyViewedProteinVersionId, versions);
      return {
        structureFile: structureInfo.structureFile,
        model: structureInfo.model,
        modelVersion: structureInfo.modelVersion,
        proteinId,
        structureProteinVersionId: structureInfo.versionId,
      };
    }

    // No current prediction and we've seen this version before - return null values
    return {
      structureFile: null,
      model: null,
      modelVersion: null,
      proteinId,
      structureProteinVersionId: null,
    };
  }
}

export function AlphaFoldOptions({
  structureFile,
  model,
  modelVersion,
  proteinId,
  structureProteinVersionId,
}: {
  structureFile: ProteinStructureExternalFile | null;
  model: string | null;
  modelVersion: string | null;
  proteinId: string;
  structureProteinVersionId: number | null;
}): JSX.Element {
  const [hasDismissedPopover, setHasDismissedPopover] = usePreference<boolean>(
    ScientificModelPreferences.HAS_DISMISSED_MODEL_CONFIDENCE_POPOVER
  );
  const [popoverProps, setPopoverProps] = useState<
    {isOpen: boolean; interaction: FloatingInteraction.CONTROLLED} | {interaction: FloatingInteraction.HOVER}
  >(() => {
    // If user has already dismissed the popover, start with hover interaction
    if (hasDismissedPopover) {
      return {interaction: FloatingInteraction.HOVER};
    }
    return {
      isOpen: true,
      interaction: FloatingInteraction.CONTROLLED,
    };
  });

  useEffect(() => {
    // Only set the timeout if the user hasn't dismissed the popover before
    if (!hasDismissedPopover) {
      const timeoutId = setTimeout(() => {
        setPopoverProps({interaction: FloatingInteraction.HOVER});
      }, 5000);
      return () => {
        clearTimeout(timeoutId);
      };
    }
    return () => {};
  }, [hasDismissedPopover]);

  const handleDismissPopover = (): void => {
    setHasDismissedPopover(true);
    setPopoverProps({interaction: FloatingInteraction.HOVER});
  };

  if (structureFile != null && model != null && modelVersion != null && structureProteinVersionId != null) {
    return (
      <div className={css(styles.optionButtonsLayout)}>
        <div className={css(styles.leftSection)}>
          <div>
            <Popover
              {...popoverProps}
              placement={FloatingPlacement.RIGHT_START}
              renderContent={() => (
                <QualityDataLegend
                  onDismiss={
                    popoverProps.interaction === FloatingInteraction.CONTROLLED
                      ? handleDismissPopover
                      : undefined
                  }
                />
              )}
            >
              <BenchlingIcon icon="info-circle" className={css(styles.infoIcon)} />
            </Popover>
            <span className={css(styles.infoText)}>Model confidence</span>
          </div>
          <StructurePredictionInfo
            modelDisplayName={getModelDisplayName(model)}
            modelVersion={modelVersion}
            className={css(styles.predictionInfo)}
          />
        </div>
        <Button
          className={css(styles.withoutButtonFocusStyling)}
          text={`Download ${
            structureFile.previewType === EXTERNAL_FILE_TYPE.MOLSTAR_MMCIF_VIEWER ? 'mmCIF (PDBx)' : 'PDB'
          }`}
          buttonStyle={ButtonStyle.PRIMARY}
          size={ButtonSize.MINI}
          onClick={() => downloadStructureFile(structureFile, proteinId, structureProteinVersionId)}
        />
      </div>
    );
  } else if (BenchlingConfig.MOLBIO_ALPHAFOLD_IS_ACADEMIC) {
    return <ModelHubExecutionCountNotice inBanner={false} />;
  } else {
    return <div className={css(styles.optionButtonsLayout)} />;
  }
}

const ConnectedAlphaFoldOptions = AlphaFoldOptionsConnector.connect(AlphaFoldOptions);
export default ConnectedAlphaFoldOptions;

const styles = StyleSheet.create({
  infoIcon: {
    color: GRAY_DARKER,
  },
  infoText: {
    ...FONT_PROXIMA_13,
    marginLeft: '8px',
  },
  optionButtonsLayout: {
    padding: '0px 7px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    // Should match the value inherited from .buffer-options
    height: '35px',
  },
  leftSection: {
    display: 'flex',
    alignItems: 'center',
    gap: '20px',
  },
  predictionInfo: {
    margin: '0',
  },
  largerText: FONT_PROXIMA_13,
  withoutButtonFocusStyling: {':focus': {textDecoration: 'none'}},
});
